import React from 'react';
import { Button, Input, Table, TagPicker, Tooltip, Whisper } from 'rsuite';
import DetailIcon from '@rsuite/icons/Detail';
import * as contactRequestsApi from '../../api/contactRequests';
import PageLayout from '../../common/PageLayout';
import ColumnsButton from '../../common/ColumnsButton';
import FilterPanel, { FilterField } from '../../common/FilterPanel';
import { useTableColumns } from '../../common/useTableColumns';
import { useResourceList } from '../../common/useResourceList';
import { FormField, FormGrid, FormModal } from '../../common/FormModal';
import { formatDateTime, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import TablePagination from '../../common/TablePagination';

const { Column, HeaderCell, Cell } = Table;

const CONTACT_TYPES = [
  { value: 'contact', label: 'Контакт' },
  { value: 'support_proposal', label: 'Пропозиція підтримки' },
];

const TABLE_COLUMNS = [
  { value: 'type', label: 'Тип', display: true, flexGrow: 1, minWidth: 120 },
  {
    value: 'email',
    label: 'Пошта',
    display: true,
    flexGrow: 2,
    minWidth: 160,
  },
  {
    value: 'firstName',
    label: "Ім'я",
    display: true,
    flexGrow: 1,
    minWidth: 120,
  },
  {
    value: 'lastName',
    label: 'Прізвище',
    display: true,
    flexGrow: 1,
    minWidth: 140,
  },
  {
    value: 'phone',
    label: 'Телефон',
    display: true,
    flexGrow: 1,
    minWidth: 130,
  },
  {
    value: 'message',
    label: 'Повідомлення',
    display: true,
    flexGrow: 3,
    minWidth: 220,
  },
  {
    value: 'createdAt',
    label: 'Створено',
    display: true,
    flexGrow: 1,
    minWidth: 130,
  },
  {
    value: 'updatedAt',
    label: 'Оновлено',
    display: false,
    flexGrow: 1,
    minWidth: 130,
  },
];

const SORTABLE_COLUMNS = new Set([
  'type',
  'email',
  'firstName',
  'lastName',
  'phone',
  'createdAt',
  'updatedAt',
]);

const MAX_MESSAGE_LENGTH = 120;

type ContactRequest = {
  id: string;
  type: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  message: string;
  createdAt?: number | null;
  updatedAt?: number | null;
};

export default function ContactRequests() {
  const clickedCellRef = React.useRef(false);
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [types, setTypes] = React.useState<string[]>([]);
  const [viewRequest, setViewRequest] = React.useState<ContactRequest | null>(
    null
  );

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'contact-requests:tableColumns'
  );

  const list = useResourceList<ContactRequest>({
    fetch: ({ page, perPage, sortColumn, sortOrder }) =>
      contactRequestsApi.list(
        page,
        perPage,
        email || undefined,
        name || undefined,
        phone || undefined,
        types.length ? types : undefined,
        message || undefined,
        sortColumn,
        sortOrder
      ),
    filters: [email, name, phone, message, types],
    initialSortColumn: 'createdAt',
  });

  // Filters live in the panel; search by name is the always-visible input.
  const activeFilterCount =
    [email, phone, message].filter(Boolean).length + (types.length ? 1 : 0);

  const resetFilters = () => {
    setEmail('');
    setPhone('');
    setMessage('');
    setTypes([]);
  };

  const renderMessage = (value?: string) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return '-';
    }
    const short =
      normalized.length > MAX_MESSAGE_LENGTH
        ? `${normalized.slice(0, MAX_MESSAGE_LENGTH - 3)}...`
        : normalized;

    if (short === normalized) {
      return normalized;
    }

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>{normalized}</Tooltip>}
      >
        <span>{short}</span>
      </Whisper>
    );
  };

  const resolveTypeLabel = (value?: string) => {
    if (!value) {
      return '-';
    }
    const match = CONTACT_TYPES.find(item => item.value === value);
    return match ? match.label : value;
  };

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  return (
    <>
      <PageLayout
        title="Контактні заявки"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за ім'ям або прізвищем"
              value={name || ''}
              onChange={setName}
            />
            <FilterPanel activeCount={activeFilterCount} onReset={resetFilters}>
              {filterContainer => (
                <>
                  <FilterField label="Пошта">
                    <Input
                      placeholder="Пошта"
                      value={email || ''}
                      onChange={setEmail}
                    />
                  </FilterField>
                  <FilterField label="Телефон">
                    <Input
                      placeholder="Телефон"
                      value={phone || ''}
                      onChange={setPhone}
                    />
                  </FilterField>
                  <FilterField label="Повідомлення">
                    <Input
                      placeholder="Повідомлення"
                      value={message || ''}
                      onChange={setMessage}
                    />
                  </FilterField>
                  <FilterField label="Тип">
                    <TagPicker
                      block
                      placeholder="Тип"
                      value={types}
                      onChange={setTypes}
                      data={CONTACT_TYPES}
                      container={filterContainer}
                      preventOverflow
                    />
                  </FilterField>
                </>
              )}
            </FilterPanel>
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
          </>
        }
      >
        <div className="section-card">
          <Table
            wordWrap="break-word"
            loading={list.loading}
            autoHeight={true}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            bordered={true}
            data={list.data}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
            onRowClick={rowData => {
              if (clickedCellRef.current) {
                clickedCellRef.current = false;
                return;
              }
              setViewRequest(rowData as ContactRequest);
            }}
            sortColumn={list.sortColumn}
            sortType={list.sortType}
            onSortColumn={(sortColumn, sortType) =>
              list.setSort(sortColumn, sortType || 'desc')
            }
          >
            {renderRowNumberColumn({ page: list.page, perPage: list.perPage })}

            {tableColumns.map(column => {
              if (column.display === false) {
                return null;
              }

              const commonProps = {
                key: column.value,
                flexGrow: column.flexGrow ?? 1,
                minWidth: column.minWidth || 80,
                sortable: SORTABLE_COLUMNS.has(column.value),
                fixed:
                  column.value === firstVisibleColumnValue
                    ? ('left' as const)
                    : undefined,
              };

              switch (column.value) {
                case 'type':
                  return (
                    <Column {...commonProps} fullText={true}>
                      <HeaderCell style={{ textAlign: 'center' }}>
                        {column.label}
                      </HeaderCell>
                      <Cell>{rowData => resolveTypeLabel(rowData.type)}</Cell>
                    </Column>
                  );
                case 'message':
                  return (
                    <Column {...commonProps} fullText={true}>
                      <HeaderCell style={{ textAlign: 'center' }}>
                        {column.label}
                      </HeaderCell>
                      <Cell className="table-cell-description">
                        {rowData => renderMessage(rowData.message)}
                      </Cell>
                    </Column>
                  );
                case 'createdAt':
                case 'updatedAt':
                  return (
                    <Column {...commonProps} fullText={true}>
                      <HeaderCell style={{ textAlign: 'center' }}>
                        {column.label}
                      </HeaderCell>
                      <Cell dataKey={column.value}>
                        {rowData => formatDateTime(rowData[column.value])}
                      </Cell>
                    </Column>
                  );
                default:
                  return (
                    <Column {...commonProps} fullText={true}>
                      <HeaderCell style={{ textAlign: 'center' }}>
                        {column.label}
                      </HeaderCell>
                      <Cell
                        dataKey={column.value}
                        className={
                          column.value === 'email' ? 'table-cell-primary' : ''
                        }
                      />
                    </Column>
                  );
              }
            })}
            <Column fixed="right" width={120}>
              <HeaderCell>Дії</HeaderCell>
              <Cell
                onClick={() => {
                  clickedCellRef.current = true;
                }}
                style={{ padding: '4px 6px', textAlign: 'center' }}
              >
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Перегляд</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        onClick={() => {
                          clickedCellRef.current = true;
                          setViewRequest(rowData as ContactRequest);
                        }}
                      >
                        <DetailIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={list.totalAmount}
            activePage={list.page}
            limit={list.perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={list.setPerPage}
            onChangePage={list.goToPage}
          />
        </div>
        <FormModal
          open={!!viewRequest}
          onClose={() => setViewRequest(null)}
          title="Перегляд заявки"
          footer={
            <Button onClick={() => setViewRequest(null)} appearance="subtle">
              Закрити
            </Button>
          }
        >
          <FormGrid>
            <FormField label="Тип">
              <Input
                value={viewRequest ? resolveTypeLabel(viewRequest.type) : ''}
                readOnly
              />
            </FormField>
            <FormField label="Пошта">
              <Input value={viewRequest?.email || ''} readOnly />
            </FormField>
            <FormField label="Телефон">
              <Input value={viewRequest?.phone || ''} readOnly />
            </FormField>
            <FormField label="Ім'я">
              <Input value={viewRequest?.firstName || ''} readOnly />
            </FormField>
            <FormField label="Прізвище">
              <Input value={viewRequest?.lastName || ''} readOnly />
            </FormField>
            <FormField label="Створено">
              <Input
                value={viewRequest ? formatDateTime(viewRequest.createdAt) : ''}
                readOnly
              />
            </FormField>
            <FormField label="Оновлено">
              <Input
                value={viewRequest ? formatDateTime(viewRequest.updatedAt) : ''}
                readOnly
              />
            </FormField>
          </FormGrid>
          <FormField label="Повідомлення">
            <Input
              as="textarea"
              rows={8}
              value={viewRequest?.message || ''}
              readOnly
            />
          </FormField>
        </FormModal>
      </PageLayout>
    </>
  );
}
